/* What is wrong with a password somebody has just typed twice.
 *
 * A pure function so the three rules are one answer rather than three conditions spread through a
 * form's JSX — and so each of them can be asserted without rendering anything.
 */

/** The one thing to tell the person, or `null` when the pair is usable.
 *
 * **One problem at a time, and the order is deliberate.** Showing "too short" and "they do not
 * match" together is two corrections for what is usually one mistake, and a reader who fixes the
 * length then sees the mismatch appear reads it as a new failure. Emptiness first because an empty
 * pair technically matches, so reporting a match on two blank fields would be true and absurd;
 * length before mismatch because somebody who mistyped a short password has to change it anyway.
 */
export type PasswordProblem = 'required' | 'tooShort' | 'mismatch';

export function passwordProblem({
  password,
  confirmation,
  minLength,
}: {
  readonly password: string;
  readonly confirmation: string;
  readonly minLength: number;
}): PasswordProblem | null {
  if (password === '' || confirmation === '') return 'required';
  if (password.length < minLength) return 'tooShort';
  if (password !== confirmation) return 'mismatch';
  return null;
}

/** Whether the platform's own policy would accept this, as far as a client can tell.
 *
 * **Deliberately only a length check, and the server is the authority.** `validate_password_strength`
 * also refuses a password containing the local part of its own email and refuses the common
 * shapes, and reimplementing that here would be two answers to "is this password acceptable" — the
 * two-vocabularies failure this codebase records at five sites, in the one place where the client's
 * answer being laxer is a bad refusal and the client's answer being stricter is a password the
 * server would have taken.
 *
 * So the client checks the one rule it can state exactly (a minimum length, which the contract
 * publishes as `minLength`) and renders the server's 422 for everything else. That is why
 * `passwordProblem` has no `weak` member: a client cannot know.
 */
export function looksLongEnough(password: string, minLength: number): boolean {
  return password.length >= minLength;
}
